import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Typography } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  AdvancedSearchableProjectDropdown,
  AdvancedSearchableTaskDropdown
} from '~/modules/common/components';
import { useMeContext } from '~/modules/me';
import BillingStatusDropdown from './BillingStatusDropdown';
import ProjectTimeEntrySummary from './TimeEntrySummary';
import { useContentStyles } from './styles';

const DestinationMoveTimeStep = ({
  moveTimeData,
  onDestinationProjectChange,
  onDestinationTaskChange,
  onBillingStatusChange,
  timeEntrySummary
}) => {
  const classes = useContentStyles();
  const intl = useIntl();

  const { hasViewProjectBillingOptions } = useMeContext();

  const isSameSourceAndDestination = useMemo(() => {
    return (
      moveTimeData.source.project?.id ===
        moveTimeData.destination.project?.id &&
      moveTimeData.source.task?.id === moveTimeData.destination.task?.id
    );
  }, [
    moveTimeData.source.project,
    moveTimeData.source.task,
    moveTimeData.destination.project,
    moveTimeData.destination.task
  ]);

  return (
    <div
      className={classes.stepContent}
      data-qe-id="MoveTimeDialogDestinationStep"
    >
      <ProjectTimeEntrySummary
        projectName={moveTimeData.source.project?.displayText}
        taskDisplayText={
          moveTimeData.source.task?.fullPathText ||
          moveTimeData.source.task?.displayText
        }
        loading={timeEntrySummary.loading}
        count={timeEntrySummary.count}
        totalHours={timeEntrySummary.totalHours}
        showExpandButton={false}
      />
      <Typography variant="h6" className={classes.sectionTitle}>
        <FormattedMessage id="moveTimeDialog.moveTimeTo" />
      </Typography>
      <div className={classes.formRow}>
        <AdvancedSearchableProjectDropdown
          value={moveTimeData.destination.project || null}
          onChange={onDestinationProjectChange}
          label={intl.formatMessage({ id: 'moveTimeDialog.project' })}
          showInactiveProjects={
            moveTimeData.destination.filter.showInactiveProjects
          }
        />
        <AdvancedSearchableTaskDropdown
          value={moveTimeData.destination.task || null}
          onChange={onDestinationTaskChange}
          label={intl.formatMessage({ id: 'moveTimeDialog.task' })}
          projectId={moveTimeData.destination.project?.id}
          disabled={!moveTimeData.destination.project}
          showClosedTasks={moveTimeData.destination.filter.showClosedTasks}
        />
      </div>
      {isSameSourceAndDestination && (
        <div className={classes.warningContainer}>
          <Alert
            severity="warning"
            className={classes.alert}
            data-qe-id="MoveTimeDialogSameProjectTaskWarning"
            icon={false}
          >
            <Typography component="div" className={classes.warningTitle}>
              <FormattedMessage id="moveTimeDialog.duplicateSelection" />
            </Typography>
            <Typography component="div">
              <FormattedMessage id="moveTimeDialog.sameProjectTaskWarning" />
            </Typography>
          </Alert>
        </div>
      )}
      {hasViewProjectBillingOptions && (
        <div className={classes.billingSection}>
          <Typography variant="h6" className={classes.sectionTitle}>
            <FormattedMessage id="moveTimeDialog.withFollowingBillingStatus" />
          </Typography>
          <div className={classes.formRow}>
            <div className={classes.halfWidthField}>
              <BillingStatusDropdown
                value={moveTimeData.billingStatus || null}
                onChange={onBillingStatusChange}
                label={intl.formatMessage({ id: 'moveTimeDialog.billing' })}
                sourceProject={moveTimeData.source.project}
                sourceTask={moveTimeData.source.task}
                destinationProject={moveTimeData.destination.project}
                destinationTask={moveTimeData.destination.task}
                data-qe-id="MoveTimeDialogBillingStatus"
                aria-label={intl.formatMessage({
                  id: 'moveTimeDialog.billing'
                })}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

DestinationMoveTimeStep.propTypes = {
  moveTimeData: PropTypes.object.isRequired,
  onDestinationProjectChange: PropTypes.func.isRequired,
  onDestinationTaskChange: PropTypes.func.isRequired,
  onBillingStatusChange: PropTypes.func.isRequired,
  timeEntrySummary: PropTypes.object.isRequired
};

export default DestinationMoveTimeStep;
